import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Input,
  Link,
  Stack,
  Typography,
} from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../lib/api";
import type { Task, TaskUpdateData, TaskAttachment } from "../../types/tasks";
import { TaskRecordPicker } from "./TaskRecordPicker";

export function TaskWorkspace({
  task,
  refresh,
}: {
  task: Task;
  refresh: () => void;
}) {
  const [text, setText] = useState("");
  const cache = useQueryClient();
  const update = useMutation({
    mutationFn: (data: TaskUpdateData) =>
      apiClient.patch(`/api/tasks/${task.id}/`, data),
    onSuccess: () => {
      refresh();
      cache.invalidateQueries({ queryKey: ["tasks"] });
      cache.invalidateQueries({ queryKey: ["taskComments", task.id] });
    },
  });
  const dependencies = useQuery({
    queryKey: ["task-dependencies", task.id, task.prerequisite_tasks],
    queryFn: () =>
      Promise.all(
        (task.prerequisite_tasks || []).map((id) =>
          apiClient.get<Task>(`/api/tasks/${id}/`).then((r) => r.data),
        ),
      ),
  });
  const files = useQuery({
    queryKey: ["task-files", task.id],
    queryFn: async () =>
      (
        await apiClient.getAllPages<TaskAttachment>(
          `/api/task-attachments/?task=${task.id}`,
        )
      ).data,
  });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const data = new FormData();
      data.append("task", String(task.id));
      data.append("file", file);
      return apiClient.post("/api/task-attachments/", data);
    },
    onSuccess: () => {
      cache.invalidateQueries({ queryKey: ["task-files", task.id] });
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: number) =>
      apiClient.delete(`/api/task-attachments/${id}/`),
    onSuccess: () => {
      cache.invalidateQueries({ queryKey: ["task-files", task.id] });
      refresh();
    },
  });
  const items = task.checklist || [];
  return (
    <Stack spacing={2}>
      {(update.isError ||
        upload.isError ||
        remove.isError ||
        dependencies.isError ||
        files.isError) && (
        <Alert color="danger">
          The change or lookup failed.{" "}
          {String(
            (update.error || upload.error || (remove.error as any))?.message ||
              "Please retry.",
          )}
          <Button
            size="sm"
            onClick={() => {
              dependencies.refetch();
              files.refetch();
            }}
          >
            Reload
          </Button>
        </Alert>
      )}
      <Typography level="title-lg">Prerequisites</Typography>
      {dependencies.data?.map((dep) => (
        <Box
          key={dep.id}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Link href={`/operations/tasks?task=${dep.id}`}>{dep.name}</Link>
          <Typography level="body-sm">
            {dep.status === "completed" ? "Completed" : "Blocking this task"}
          </Typography>
          <Button
            size="sm"
            variant="plain"
            disabled={update.isPending}
            onClick={() =>
              update.mutate({
                prerequisite_tasks: task.prerequisite_tasks.filter(
                  (id) => id !== dep.id,
                ),
              })
            }
          >
            Remove prerequisite
          </Button>
        </Box>
      ))}
      {!task.prerequisite_tasks?.length && (
        <Typography level="body-sm">No prerequisites</Typography>
      )}
      <TaskRecordPicker
        label="Add prerequisite"
        endpoint="/api/tasks/"
        disabled={update.isPending}
        onChange={(record) => {
          if (record)
            update.mutate({
              prerequisite_tasks: [
                ...new Set([...(task.prerequisite_tasks || []), record.id]),
              ],
            });
        }}
      />
      <Typography level="title-lg">
        Checklist · {items.filter((item) => item.done).length}/{items.length}
      </Typography>
      {items.map((item) => (
        <Box
          key={item.id}
          sx={{ display: "flex", gap: 1, alignItems: "center" }}
        >
          <Checkbox
            sx={{ flex: 1 }}
            label={item.text}
            checked={item.done}
            disabled={update.isPending}
            onChange={(e) =>
              update.mutate({
                checklist: items.map((row) =>
                  row.id === item.id ? { ...row, done: e.target.checked } : row,
                ),
              })
            }
          />
          <Button
            size="sm"
            variant="plain"
            disabled={update.isPending}
            onClick={() =>
              update.mutate({
                checklist: items.filter((row) => row.id !== item.id),
              })
            }
          >
            Remove
          </Button>
        </Box>
      ))}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Input
          sx={{ flex: 1 }}
          placeholder="New checklist item"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Button
          disabled={!text.trim() || update.isPending}
          onClick={() =>
            update.mutate(
              {
                checklist: [
                  ...items,
                  { id: crypto.randomUUID(), text: text.trim(), done: false },
                ],
              },
              { onSuccess: () => setText("") },
            )
          }
        >
          Add
        </Button>
      </Box>
      <Typography level="title-lg">Attachments</Typography>
      <Button component="label" variant="outlined" loading={upload.isPending}>
        Upload attachment
        <input
          type="file"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate(file);
            e.target.value = "";
          }}
        />
      </Button>
      {files.data?.map((file) => (
        <Box
          key={file.id}
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Link
            href={file.file_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ overflowWrap: "anywhere", flex: 1 }}
          >
            {file.original_filename}
          </Link>
          <Typography level="body-xs">
            {file.uploaded_by_name} ·{" "}
            {new Date(file.created_at).toLocaleDateString()}
          </Typography>
          <Button
            size="sm"
            color="danger"
            variant="plain"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Delete ${file.original_filename}?`))
                remove.mutate(file.id);
            }}
          >
            Delete
          </Button>
        </Box>
      ))}
      {files.data?.length === 0 && (
        <Typography level="body-sm">No attachments yet.</Typography>
      )}
    </Stack>
  );
}
